// Which experiences an offer appears in.
//
// An offer used to belong to exactly one experience, stored as a single
// `experienceId` string where '' meant the default. It can now belong to
// several, stored as `experienceIds: { <realId>: true }`.
//
// THE OLD FIELD IS STILL WRITTEN, ON PURPOSE. team_app_react_native is a
// shipped mobile build: phones running the current version know nothing about
// `experienceIds` and read `experienceId`. Keeping it populated means an old
// build shows the offer in one experience instead of losing it — repurposing
// `experienceId` (say, to '*') would have made it match nothing and vanish for
// every fan who has not updated.
//
// matchesExperience below MUST stay in step with the same function in
// team_app_react_native/src/screens/ProfileScreen.js. The tests here encode
// the contract; the fan side has the other half.

// The ids an offer targets. Empty array means "use the legacy single field".
export function selectedExperienceIds(offer) {
    const map = offer && offer.experienceIds;
    if (!map || typeof map !== 'object') return [];
    return Object.keys(map).filter(id => map[id]);
}

/**
 * Does this offer appear in this experience?
 * @param offer      the offer record
 * @param experience the experience being viewed, or null when a tenant has
 *                   none configured at all (legacy root config)
 */
export function offerMatchesExperience(offer, experience) {
    const ids = selectedExperienceIds(offer);
    if (ids.length > 0) {
        // A tenant with no experiences configured runs the root config, which
        // an explicitly-targeted offer cannot match.
        return experience ? ids.indexOf(experience.id) !== -1 : false;
    }

    // Legacy single-value behaviour, unchanged.
    const offerExpId = (offer && offer.experienceId) || '';
    if (!experience) return offerExpId === '';
    if (experience.isDefault) return offerExpId === '' || offerExpId === experience.id;
    return offerExpId === experience.id;
}

/**
 * What to write for a set of chosen experiences.
 *
 * `experienceId` is the one an old build will show the offer in. It prefers
 * the experience you were working in when you saved, so the offer lands where
 * you were looking rather than somewhere arbitrary.
 *
 * @param selectedIds        real experience ids the offer should appear in
 * @param experiences        the full list, to resolve which one is default
 * @param preferredPrimaryId the scope being edited ('' for the default)
 */
export function experienceFieldsFor(selectedIds, experiences, preferredPrimaryId) {
    const ids = (selectedIds || []).filter(Boolean);
    if (ids.length === 0) return { experienceId: '', experienceIds: null };

    const list = experiences || [];
    const defaultExp = list.find(exp => exp && exp.isDefault);
    // '' as a preferred id means the default experience; resolve it to its real id
    const preferredReal = preferredPrimaryId || (defaultExp ? defaultExp.id : '');

    const primary = ids.indexOf(preferredReal) !== -1 ? preferredReal : ids[0];
    const primaryIsDefault = !!(defaultExp && primary === defaultExp.id);

    const experienceIds = {};
    ids.forEach(id => { experienceIds[id] = true; });

    return {
        // an old build reads this one; '' keeps the default experience's
        // existing meaning intact
        experienceId: primaryIsDefault ? '' : primary,
        experienceIds
    };
}

/**
 * Which experiences an offer is currently in, as real ids, for pre-checking
 * the boxes when you open one to edit.
 */
export function currentSelectionFor(offer, experiences) {
    const explicit = selectedExperienceIds(offer);
    if (explicit.length > 0) return explicit;

    const list = experiences || [];
    const defaultExp = list.find(exp => exp && exp.isDefault);
    const legacy = (offer && offer.experienceId) || '';
    if (!legacy) return defaultExp ? [defaultExp.id] : [];
    return [legacy];
}

// Editing an offer that appears in more than one experience changes it in all
// of them, so the list has to say so rather than let someone find out.
export function isSharedAcrossExperiences(offer) {
    return selectedExperienceIds(offer).length > 1;
}
